// src/pages/Users/ResetPassword.jsx

import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Container, Paper, Avatar, Typography, TextField, Button, Alert,
  Link, Fade, InputAdornment, IconButton, CircularProgress, LinearProgress,
} from '@mui/material';
import {
  LockReset, LockOutlined, Visibility, VisibilityOff, CheckCircle,
  RadioButtonUnchecked, LinkOff, TaskAlt, ArrowBack, ArrowForward,
} from '@mui/icons-material';
import userApi from '../../api/userApi';

// ───────────────────────── تصميم مشترك مع صفحة الدخول ─────────────────────────
const pageSx = {
  position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 9999, overflow: 'auto', p: 2,
  background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 40%, #2e1065 100%)',
  '&::before': {
    content: '""', position: 'absolute', inset: 0, zIndex: 0,
    backgroundImage: `
      radial-gradient(circle at 20% 20%, rgba(99,102,241,.25) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(16,185,129,.15) 0%, transparent 50%)`,
  },
};

const cardSx = {
  position: 'relative', overflow: 'hidden', width: '100%',
  p: { xs: 4, sm: 5 }, borderRadius: '28px',
  display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
  background: 'rgba(255,255,255,.98)',
  boxShadow: '0 40px 80px -20px rgba(0,0,0,.6)',
};

const topBarSx = {
  position: 'absolute', top: 0, left: 0, height: 6, width: '100%',
  background: 'linear-gradient(90deg, #4F46E5 0%, #10B981 100%)',
};

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '14px', bgcolor: '#f8fafc' },
};

const primaryBtnSx = {
  py: 1.5, fontSize: '1rem', fontWeight: 700, borderRadius: '14px',
  background: 'linear-gradient(90deg, #4F46E5 0%, #6366F1 100%)',
  boxShadow: '0 10px 25px -6px rgba(79,70,229,.4)',
  '&:hover': { background: 'linear-gradient(90deg, #3730A3 0%, #4F46E5 100%)' },
  '&.Mui-disabled': { background: '#e2e8f0', boxShadow: 'none' },
};

const StateIcon = ({ color, bg, children }) => (
  <Box sx={{
    mt: 1, mb: 2.5, width: 84, height: 84, borderRadius: '50%', bgcolor: bg,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    '& svg': { fontSize: 44, color },
  }}>
    {children}
  </Box>
);

export default function ResetPassword() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const rules = [
    { ok: password.length >= 8, label: t('auth.rule_length', '8 أحرف على الأقل') },
    { ok: /[A-Za-z\u0600-\u06FF]/.test(password), label: t('auth.rule_letters', 'حروف') },
    { ok: /\d/.test(password), label: t('auth.rule_digits', 'أرقام') },
  ];
  const score = rules.filter(r => r.ok).length + (password.length >= 12 ? 1 : 0);
  const strength = [
    { label: '', color: '#e2e8f0' },
    { label: t('auth.weak', 'ضعيفة'), color: '#ef4444' },
    { label: t('auth.medium', 'متوسطة'), color: '#f59e0b' },
    { label: t('auth.good', 'جيدة'), color: '#10b981' },
    { label: t('auth.strong', 'قوية جدًا'), color: '#059669' },
  ][score];

  const allRulesOk = rules.every(r => r.ok);
  const matches = password !== '' && password === confirm;

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    userApi.validateResetToken(token)
      .then(res => setValid(Boolean(res.data?.valid)))
      .catch(() => setValid(false))
      .finally(() => setChecking(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!allRulesOk) return setError(t('auth.password_rules_failed', 'كلمة المرور لا تستوفي الشروط'));
    if (!matches) return setError(t('auth.passwords_not_match', 'كلمتا المرور غير متطابقتين'));

    setLoading(true);
    try {
      await userApi.resetPassword({ token, newPassword: password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (err) {
      setError(err.response?.data?.message || t('auth.request_failed', 'حدث خطأ، حاول مرة أخرى'));
    } finally {
      setLoading(false);
    }
  };

  const eye = (
    <InputAdornment position="end">
      <IconButton onClick={() => setShow(s => !s)} edge="end" size="small" tabIndex={-1}>
        {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );
  const lockIcon = (
    <InputAdornment position="start">
      <LockOutlined sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
    </InputAdornment>
  );

  const BackIcon = isRTL ? ArrowForward : ArrowBack;

  const renderContent = () => {
    // ── جارٍ التحقق من الرابط ──
    if (checking) {
      return (
        <Box sx={{ py: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={36} />
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {t('auth.verifying_link', 'جارٍ التحقق من الرابط...')}
          </Typography>
        </Box>
      );
    }

    // ── رابط غير صالح ──
    if (!valid) {
      return (
        <Fade in>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <StateIcon color="#ef4444" bg="rgba(239,68,68,.1)"><LinkOff /></StateIcon>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', mb: 1 }}>
              {t('auth.link_expired_title', 'انتهت صلاحية الرابط')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 4, lineHeight: 1.8 }}>
              {t('auth.reset_link_invalid', 'الرابط غير صالح أو انتهت مدته أو استُخدم مسبقًا. اطلب رابطًا جديدًا للمتابعة.')}
            </Typography>
            <Button fullWidth variant="contained" component={RouterLink} to="/forgot-password" sx={primaryBtnSx}>
              {t('auth.request_new_link', 'طلب رابط جديد')}
            </Button>
          </Box>
        </Fade>
      );
    }

    // ── تم بنجاح ──
    if (done) {
      return (
        <Fade in>
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <StateIcon color="#10B981" bg="rgba(16,185,129,.1)"><TaskAlt /></StateIcon>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', mb: 1 }}>
              {t('auth.password_reset_success', 'تم تغيير كلمة المرور')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 3, lineHeight: 1.8 }}>
              {t('auth.redirecting_to_login', 'يمكنك الآن تسجيل الدخول بكلمة المرور الجديدة. جارٍ تحويلك...')}
            </Typography>
            <LinearProgress sx={{ width: '60%', borderRadius: 2, mb: 3 }} />
            <Button fullWidth variant="contained" component={RouterLink} to="/login" replace sx={primaryBtnSx}>
              {t('auth.login_now', 'تسجيل الدخول الآن')}
            </Button>
          </Box>
        </Fade>
      );
    }

    // ── النموذج ──
    return (
      <>
        <Avatar sx={{
          mt: 1, mb: 2, width: 60, height: 60,
          background: 'linear-gradient(135deg, #4F46E5 0%, #3730A3 100%)',
          boxShadow: '0 12px 24px -4px rgba(79,70,229,.4)',
        }}>
          <LockReset />
        </Avatar>

        <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', mb: 1 }}>
          {t('auth.reset_password_title', 'تعيين كلمة مرور جديدة')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 4 }}>
          {t('auth.reset_password_hint', 'اختر كلمة مرور قوية لم تستخدمها من قبل')}
        </Typography>

        {error && (
          <Alert severity="error" variant="filled" sx={{ width: '100%', mb: 3, borderRadius: '12px', fontWeight: 600 }}>
            {error}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
          <TextField
            fullWidth
            autoFocus
            type={show ? 'text' : 'password'}
            label={t('auth.new_password', 'كلمة المرور الجديدة')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            inputProps={{ dir: 'ltr' }}
            InputProps={{ startAdornment: lockIcon, endAdornment: eye }}
            sx={inputSx}
          />

          {/* مؤشر القوة */}
          <Box sx={{ mt: 1.5, mb: 1 }}>
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              {[1, 2, 3, 4].map(i => (
                <Box key={i} sx={{
                  flex: 1, height: 5, borderRadius: 3,
                  bgcolor: i <= score ? strength.color : '#e2e8f0',
                  transition: 'background-color .3s',
                }} />
              ))}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, minHeight: 20 }}>
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {rules.map(r => (
                  <Box key={r.label} sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5,
                    color: r.ok ? '#10b981' : 'text.disabled', transition: 'color .3s',
                  }}>
                    {r.ok ? <CheckCircle sx={{ fontSize: 15 }} /> : <RadioButtonUnchecked sx={{ fontSize: 15 }} />}
                    <Typography variant="caption" fontWeight={600}>{r.label}</Typography>
                  </Box>
                ))}
              </Box>
              {password && (
                <Typography variant="caption" fontWeight={800} sx={{ color: strength.color }}>
                  {strength.label}
                </Typography>
              )}
            </Box>
          </Box>

          <TextField
            fullWidth
            type={show ? 'text' : 'password'}
            label={t('auth.confirm_password', 'تأكيد كلمة المرور')}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={confirm !== '' && !matches}
            helperText={confirm !== '' && !matches ? t('auth.passwords_not_match', 'كلمتا المرور غير متطابقتين') : undefined}
            inputProps={{ dir: 'ltr' }}
            InputProps={{
              startAdornment: lockIcon,
              endAdornment: matches ? (
                <InputAdornment position="end"><CheckCircle sx={{ color: '#10b981', fontSize: 20 }} /></InputAdornment>
              ) : undefined,
            }}
            sx={{ ...inputSx, mt: 2, mb: 3 }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading || !allRulesOk || !matches}
            sx={primaryBtnSx}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : t('auth.save_password', 'حفظ كلمة المرور')}
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Box sx={pageSx} dir={isRTL ? 'rtl' : 'ltr'}>
      <Fade in timeout={800}>
        <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
          <Paper elevation={0} sx={cardSx}>
            <Box sx={topBarSx} />

            {renderContent()}

            {!done && (
              <Link
                component={RouterLink}
                to="/login"
                sx={{
                  mt: 3, display: 'inline-flex', alignItems: 'center', gap: 0.75,
                  fontWeight: 700, fontSize: 14, textDecoration: 'none', color: 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <BackIcon sx={{ fontSize: 18 }} />
                {t('auth.back_to_login', 'العودة لتسجيل الدخول')}
              </Link>
            )}
          </Paper>
        </Container>
      </Fade>
    </Box>
  );
}